import { create } from "zustand"

const STORAGE_PREFIX = "it2tv:agentColumnCollapsed:"

export const AGENT_COLUMN_SECTIONS = [
  "characters",
  "locations",
  "scenes3d",
  "props",
  "storyboard",
  "flow",
  "canvas",
  "overview",
  "script",
  "tasks",
  "skills",
  "company_skills",
  "studio_skills",
  "studio",
  "projects",
  "feed",
  "project_feed",
] as const

export type AgentColumnSection = (typeof AGENT_COLUMN_SECTIONS)[number]

type AgentColumnState = {
  userId: number | null
  section: AgentColumnSection | null
  collapsed: boolean
  busy: boolean
  /** null — нет непрочитанного. Пустая строка — ответ без текста, тултип берёт запасную подпись. */
  unreadPreview: string | null
  register: (userId: number | null, section: AgentColumnSection) => void
  unregister: (section: AgentColumnSection) => void
  toggle: () => void
  setBusy: (busy: boolean) => void
  noteReply: (preview: string) => void
}

function storageKey(userId: number): string {
  return `${STORAGE_PREFIX}${userId}`
}

/** Разделы, где ассистент по умолчанию свёрнут: лента — обзор, колонка ассистента ей мешает. */
const COLLAPSED_BY_DEFAULT: Partial<Record<AgentColumnSection, true>> = { feed: true }

/** Выбор человека по разделу; в хранилище только отличия от умолчания. */
function readCollapsedMap(userId: number): Partial<Record<AgentColumnSection, boolean>> {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return {}
    const out: Partial<Record<AgentColumnSection, boolean>> = {}
    for (const section of AGENT_COLUMN_SECTIONS) {
      const value = (parsed as Record<string, unknown>)[section]
      if (typeof value === "boolean") out[section] = value
    }
    return out
  } catch {
    return {}
  }
}

function isSectionCollapsed(userId: number | null, section: AgentColumnSection): boolean {
  const fallback = COLLAPSED_BY_DEFAULT[section] === true
  if (userId == null) return fallback
  return readCollapsedMap(userId)[section] ?? fallback
}

function writeSectionCollapsed(userId: number, section: AgentColumnSection, collapsed: boolean) {
  try {
    const map = readCollapsedMap(userId)
    if (collapsed === (COLLAPSED_BY_DEFAULT[section] === true)) delete map[section]
    else map[section] = collapsed
    localStorage.setItem(storageKey(userId), JSON.stringify(map))
  } catch {
    /* SSR / private mode */
  }
}

/** Непрочитанный ответ живёт только в сессии, отдельно по разделу. */
const unreadBySection = new Map<AgentColumnSection, string>()

export const useAgentColumnStore = create<AgentColumnState>((set, get) => ({
  userId: null,
  section: null,
  collapsed: false,
  busy: false,
  unreadPreview: null,
  register: (userId, section) => {
    set((state) => ({
      userId,
      section,
      collapsed: isSectionCollapsed(userId, section),
      busy: state.section === section ? state.busy : false,
      unreadPreview: unreadBySection.has(section) ? (unreadBySection.get(section) ?? "") : null,
    }))
  },
  unregister: (section) => {
    set((state) => {
      if (state.section !== section) return state
      return { section: null, collapsed: false, busy: false, unreadPreview: null }
    })
  },
  toggle: () => {
    const { userId, section, collapsed } = get()
    if (!section) return
    const next = !collapsed
    if (userId != null) writeSectionCollapsed(userId, section, next)
    if (!next) unreadBySection.delete(section)
    set({
      collapsed: next,
      unreadPreview: next ? (unreadBySection.get(section) ?? null) : null,
    })
  },
  setBusy: (busy) => set({ busy }),
  noteReply: (preview) => {
    const { section, collapsed } = get()
    if (!section || !collapsed) return
    const text = preview.trim()
    unreadBySection.set(section, text)
    set({ unreadPreview: text })
  },
}))
